import React, { useEffect, useMemo, useState } from 'react';
import adminContactService from '../services/adminContactService';

const StatusBadge = ({ status }) => {
  const color = {
    'new': 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
    'in-progress': 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
    'resolved': 'bg-green-500/20 text-green-300 border border-green-500/30',
    'closed': 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
  }[status] || 'bg-gray-500/20 text-gray-300 border border-gray-500/30';
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${color} capitalize`}>
      {status.replace('-', ' ')}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const color = {
    'low': 'bg-gray-500/20 text-gray-300 border border-gray-500/30',
    'medium': 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
    'high': 'bg-orange-500/20 text-orange-300 border border-orange-500/30',
    'urgent': 'bg-red-500/20 text-red-300 border border-red-500/30'
  }[priority] || 'bg-gray-500/20 text-gray-300 border border-gray-500/30';
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${color} capitalize`}>
      {priority}
    </span>
  );
};

const StatCard = ({ title, value, icon, gradient = 'from-purple-500 to-indigo-600' }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const duration = 1500;
    const steps = 30;
    const stepValue = value / steps;
    const stepTime = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      setDisplayValue(Math.floor(stepValue * currentStep));
      
      if (currentStep >= steps) {
        clearInterval(timer);
        setDisplayValue(value);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm font-medium mb-1">{title}</p>
          <p className="text-2xl md:text-3xl font-bold text-white font-AfacadFluxLight">
            {displayValue.toLocaleString()}
          </p>
        </div>
        <div className={`w-12 h-12 bg-gradient-to-r ${gradient} rounded-xl flex items-center justify-center`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalContacts: 0 });

  const filters = useMemo(() => ({
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(priority ? { priority } : {}),
  }), [search, status, priority]);

  const fetchStats = async () => {
    setStatsLoading(true);
    const result = await adminContactService.getStats();
    if (result.success) {
      setStats(result.data);
    }
    setStatsLoading(false);
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    const result = await adminContactService.getAllContacts(filters, { page, limit, sortBy: 'createdAt', sortOrder: 'desc' });
    if (result.success) {
      setContacts(result.data || []);
      setPagination(result.pagination || { currentPage: 1, totalPages: 1, totalContacts: 0 });
    } else {
      setError(result.error || 'Failed to load contacts');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters]);

  return (
    <div className="min-h-screen bg-slate-900 pt-32 pb-16 relative overflow-hidden">
      {/* Organic gradient overlays matching the theme */}
      <div className="absolute -top-40 -right-40 w-[1000px] h-[1000px]">
        <div 
          className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-purple-500/40 via-indigo-500/30 to-blue-500/20 rounded-full blur-3xl"
          style={{ animation: 'float 8s ease-in-out infinite' }}
        ></div>
        <div 
          className="absolute top-40 right-40 w-[600px] h-[600px] bg-gradient-to-tl from-indigo-400/30 via-purple-400/20 to-violet-400/15 rounded-full blur-2xl"
          style={{ animation: 'floatReverse 10s ease-in-out infinite' }}
        ></div>
        <div 
          className="absolute top-80 right-80 w-[500px] h-[500px] bg-gradient-to-br from-purple-300/25 via-indigo-300/20 to-blue-300/15 rounded-full blur-xl"
          style={{ animation: 'drift 12s ease-in-out infinite' }}
        ></div>
      </div>

      {/* Bottom left gradient */}
      <div className="absolute -bottom-40 -left-40 w-[900px] h-[900px]">
        <div 
          className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-gradient-to-br from-indigo-500/35 via-purple-500/25 to-violet-500/20 rounded-full blur-3xl"
          style={{ animation: 'floatLeft 14s ease-in-out infinite' }}
        ></div>
      </div>

      {/* Ambient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-transparent to-purple-900/5"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-AfacadFluxLight">
            Contact <span className="text-purple-400">Submissions</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl leading-relaxed">
            Monitor and manage contact form submissions from potential clients
          </p>
        </div>

        {/* Stats Cards */}
        {!statsLoading && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <StatCard
              title="Total Contacts"
              value={stats.overview?.totalContacts || 0}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
              gradient="from-purple-500 to-indigo-600"
            />
            <StatCard
              title="New Submissions"
              value={stats.overview?.newContacts || 0}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              gradient="from-blue-500 to-cyan-600"
            />
            <StatCard
              title="In Progress"
              value={stats.overview?.inProgressContacts || 0}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              gradient="from-yellow-500 to-orange-600"
            />
            <StatCard
              title="Resolved"
              value={stats.overview?.resolvedContacts || 0}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              gradient="from-green-500 to-emerald-600"
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <input
              type="text"
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Search name, email, message..."
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
            />
            <select
              value={status}
              onChange={(e) => { setPage(1); setStatus(e.target.value); }}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
            >
              <option value="" className="bg-slate-800 text-white">All Statuses</option>
              <option value="new" className="bg-slate-800 text-white">New</option>
              <option value="in-progress" className="bg-slate-800 text-white">In Progress</option>
              <option value="resolved" className="bg-slate-800 text-white">Resolved</option>
              <option value="closed" className="bg-slate-800 text-white">Closed</option>
            </select>
            <select
              value={priority}
              onChange={(e) => { setPage(1); setPriority(e.target.value); }}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
            >
              <option value="" className="bg-slate-800 text-white">All Priorities</option>
              <option value="low" className="bg-slate-800 text-white">Low</option>
              <option value="medium" className="bg-slate-800 text-white">Medium</option>
              <option value="high" className="bg-slate-800 text-white">High</option>
              <option value="urgent" className="bg-slate-800 text-white">Urgent</option>
            </select>
            <select
              value={limit}
              onChange={(e) => { setPage(1); setLimit(parseInt(e.target.value, 10)); }}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
            >
              <option value={10} className="bg-slate-800 text-white">10 / page</option>
              <option value={20} className="bg-slate-800 text-white">20 / page</option>
              <option value={50} className="bg-slate-800 text-white">50 / page</option>
            </select>
          </div>
        </div>

        {/* Contact Table */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left text-sm font-semibold text-gray-300 px-6 py-4">Name</th>
                  <th className="text-left text-sm font-semibold text-gray-300 px-6 py-4">Email</th>
                  <th className="text-left text-sm font-semibold text-gray-300 px-6 py-4">Region</th>
                  <th className="text-left text-sm font-semibold text-gray-300 px-6 py-4">Priority</th>
                  <th className="text-left text-sm font-semibold text-gray-300 px-6 py-4">Status</th>
                  <th className="text-left text-sm font-semibold text-gray-300 px-6 py-4">Submitted</th>
                  <th className="text-left text-sm font-semibold text-gray-300 px-6 py-4">Message</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-6 py-8 text-center text-gray-400" colSpan="7">
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-purple-400" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading submissions...
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td className="px-6 py-8 text-center text-red-400" colSpan="7">
                      <div className="flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        {error}
                      </div>
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td className="px-6 py-8 text-center text-gray-400" colSpan="7">
                      No contact submissions found.
                    </td>
                  </tr>
                ) : (
                  contacts.map((c) => (
                    <tr key={c._id} className="border-t border-white/10 hover:bg-white/5 transition-colors duration-200">
                      <td className="px-6 py-4 text-sm font-medium text-white">{c.firstName} {c.lastName}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">{c.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-300 capitalize">{c.region?.replace('-', ' ')}</td>
                      <td className="px-6 py-4"><PriorityBadge priority={c.priority} /></td>
                      <td className="px-6 py-4"><StatusBadge status={c.status} /></td>
                      <td className="px-6 py-4 text-sm text-gray-300">{new Date(c.createdAt).toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm text-gray-300 max-w-xs truncate" title={c.message}>{c.message}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Page {pagination.currentPage} of {pagination.totalPages} — {pagination.totalContacts} total contacts
          </div>
          <div className="flex space-x-3">
            <button
              className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <button
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              onClick={() => setPage((p) => (p < (pagination.totalPages || 1) ? p + 1 : p))}
              disabled={page >= (pagination.totalPages || 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* CSS Animations matching the theme */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(120px, -120px) rotate(6deg);
          }
          66% {
            transform: translate(-80px, 80px) rotate(-3deg);
          }
        }

        @keyframes floatReverse {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(-100px, 100px) rotate(-4deg);
          }
          66% {
            transform: translate(140px, -60px) rotate(3deg);
          }
        }

        @keyframes drift {
          0%, 100% {
            transform: translate(0px, 0px) scale(1);
          }
          25% {
            transform: translate(60px, -80px) scale(1.15);
          }
          50% {
            transform: translate(-40px, 40px) scale(0.85);
          }
          75% {
            transform: translate(80px, 60px) scale(1.08);
          }
        }

        @keyframes floatLeft {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(-72px, 88px) rotate(-3deg);
          }
          66% {
            transform: translate(100px, -72px) rotate(2.5deg);
          }
        }
      `}</style>
    </div>
  );
};

export default AdminContacts;


